/*
# -----------------------------------------------------------------------------
# Component: paymentAjaxMixin.js
# Author: antonio.ramirez@pst.sg
# Created Date: 12/May/2019
# Comments: Responsible for all backend related calls for Manage Payment
# -----------------------------------------------------------------------------
*/
import MessageMixin from '@/mixins/message'
import { expandURI, filterQuery } from '@/utils/ajax'
import { formatDisplayDate, formatDisplayDateTime2, formatDisplayDateTime4, removeTime } from '@/utils/date'
import clone from 'clone'
import { formatPrice } from '@/utils/index'
import { removeFormatNumber } from '@/utils/number'
import { MATCH_LOAD_DATA, CHG_ACCT_MCH_RECKEY } from '@/store/types'
import { recordLockMixin } from '@/mixins/common/recordLockMixin'
import { accountsAjaxCalls } from '@/mixins/accounting/accountsAjaxCalls'
import { strJoin } from '@/utils/stringutil'

export const paymentAjaxMixin = {
  data() {
    return {
      actionName: ''
    }
  },
  created() {
    // AR: Initialize CommonUtil Menu 29-Jan-2021
    this.initCommonUtilMenu()
  },
  methods: {
    async performSearch() {
      const apiUrl = this.$store.state.payment.menu.performSearch
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.pagination
          const paramMap = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.paymentForm)

          if (massageFilters.transactionDateFrom !== null && massageFilters.transactionDateFrom !== '') {
            massageFilters.transactionDateFrom = formatDisplayDateTime2(removeTime(massageFilters.transactionDateFrom))
          }

          if (massageFilters.transactionDateTo !== null && massageFilters.transactionDateTo !== '') {
            massageFilters.transactionDateTo = formatDisplayDateTime2(removeTime(massageFilters.transactionDateTo))
          }
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, paramMap), massageFilters, this.pagination.sortBy, this.pagination.sortDesc))
          const { data } = result.data
          if (data) {
            this.totalItems = result.data.totalElements
            this.items = []
            data.map((result) => {
              const resultList = {
                branch: result.branch,
                accountNo: result.acctNo,
                documentNo: result.docNo,
                suffix: result.suffix,
                fund: result.fund,
                type: result.type,
                tranDate: this.formatDate(result.tranDate),
                currency: result.curr,
                amount: formatPrice(result.debitAmt),
                gst: formatPrice(result.othGST),
                bankOrigAmt: formatPrice(result.bankOrigAmt),
                bankLocTot: formatPrice(result.bankLocTotal)
              }
              this.items.push(resultList)
            })
          } else {
            this.items = []
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        } finally {
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        this.loading = false
      }
    },
    async performSave() {
      const apiURI = this.$store.state.payment.menu.performAction
      if (apiURI) {
        try {
          let messageFilters = clone(this.paymentForm)
          if (messageFilters.transactionDate !== '') {
            messageFilters.transactionDate = formatDisplayDate(messageFilters.transactionDate)
          }
          const result = await this.$http.post(apiURI, messageFilters)
          const { data } = result
          if (data) {
            let docNo = this.getDocumentNo(this.paymentForm.documentNo, data.resultMap.documentNo, this.paymentForm.actionName)
            this.paymentForm.documentNo = data.resultMap.documentNo

            let recKey = strJoin('|', this.paymentForm.transactionType, this.paymentForm.branch,
              this.paymentForm.accountNo, docNo, this.paymentForm.fund, this.paymentForm.accountNoSuffix)

            if (this.paymentForm.actionName === 'CREATE_MATCH_NOW') {
              this.$store.commit(CHG_ACCT_MCH_RECKEY, recKey)
              this.checkRecordLock(recKey, 'ACT')
            }

            if (this.paymentForm.printDocument === 'I' && (this.paymentForm.actionName === 'CREATE_SAVE_POST' ||
            this.paymentForm.actionName === 'UPDATE_SAVE_POST' || this.paymentForm.actionName === 'CREATE_MATCH_LATER' ||
            this.paymentForm.actionName === 'UPDATE_MATCH_LATER')) {
              const commit = {
                branch: this.paymentForm.branch,
                accountNo: this.paymentForm.accountNo,
                type: this.paymentForm.transactionType,
                documentNo: this.paymentForm.documentNo,
                fund: this.paymentForm.fund,
                suffix: this.paymentForm.accountNoSuffix,
                actionName: this.paymentForm.actionName,
                printDoc: this.paymentForm.printDocument
              }
              this.doExportAcctPaytDoc(commit)
            }
            this.notify(data)
            if (!(this.paymentForm.actionName === 'CREATE_SAVE_POST' ||
              this.paymentForm.actionName === 'CREATE_SAVE_REVIEW' ||
              this.paymentForm.actionName === 'CREATE_MATCH_NOW' ||
              this.paymentForm.actionName === 'UPDATE_MATCH_NOW') &&
              (docNo !== '' && docNo !== undefined && docNo !== null)) {
              // AR: OS-660 Unlock Record upon exit 29-Jan-2021
              this.unlockRecord(recKey, 'ACT')
              this.$store.commit(CHG_ACCT_MCH_RECKEY, '')
            }
            this.doExit()
            if (this.paymentForm.actionName === 'CREATE_MATCH_NOW' || this.paymentForm.actionName === 'UPDATE_MATCH_NOW') {
              this.doMatchNow(this.paymentForm)
            } else {
              this.$router.push({ name: 'payment' })
            }
          }
        } catch (err) {
          const { data } = err.response
          this.showPromptLeave = true
          if (data) {
            this.processingFlag = false
            this.message(data)
            this.fieldMessage(data)
          }
        }
      } else {
        this.showPromptLeave = true
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async performReview() {
      const apiURI = this.$store.state.payment.menu.performAction
      if (apiURI) {
        try {
          this.paymentForm.actionName = 'PAYMENT_REVIEW'
          this.paymentForm.branch = this.paymentData.branch
          this.paymentForm.accountNo = this.paymentData.accountNo
          this.paymentForm.transactionType = this.paymentData.type
          this.paymentForm.documentNo = this.paymentData.documentNo
          this.paymentForm.fund = this.paymentData.fund
          this.paymentForm.accountNoSuffix = this.paymentData.suffix
          const response = await this.$http.post(apiURI, this.paymentForm)
          const { data } = response.data
          if (data) {
            this.$set(this, 'paymentForm', data.accounts)
            this.paymentForm.amount = formatPrice(data.accounts.amount)
            this.paymentForm.gstAmount = formatPrice(data.accounts.gstAmount)
            this.paymentForm.netAmount = formatPrice(data.accounts.netAmount)
            this.paymentForm.bankAmount = formatPrice(data.accounts.bankAmount)
            this.paymentForm.bankCharges = formatPrice(data.accounts.bankCharges)
            this.paymentForm.bankTotalLocal = formatPrice(data.accounts.bankTotalLocal)
            this.paymentForm.docAE = data.accounts.docAE === 'true'
            this.paymentForm.gstAmountAE = data.accounts.gstAmountAE === 'true'
            this.paymentForm.bankAmountAE = data.accounts.bankAmountAE === 'true'
            this.paymentForm.bankTotalLocalAE = data.accounts.bankTotalLocalAE === 'true'
            this.paymentForm.prtCheque = data.accounts.prtCheque === 'true'
            this.paymentForm.fromBank = data.accounts.bankNo
            this.paymentForm.transactionDate = data.accounts.transactionDate
            if (data.accounts.bankCharges === null || data.accounts.bankCharges === 'NaN') {
              this.paymentForm.bankCharges = '0.00'
            }
            if (data.accounts.gstRate === null || data.accounts.gstRate === 'NaN') {
              this.paymentForm.gstRate = '0.00'
            }
            if (data.accounts.netAmount === null || data.accounts.netAmount === 'NaN') {
              this.paymentForm.netAmount = '0.00'
            }
            if (data.accounts.bankAmount !== '' && data.accounts.bankAmount !== null) {
              this.paymentForm.bankTotalAmount = formatPrice(Number(removeFormatNumber(data.accounts.bankAmount)) + Number(removeFormatNumber(data.accounts.bankCharges)))
            }
            if (data.accounts.fund === null) {
              this.paymentForm.oldFund = ''
            } else {
              this.paymentForm.oldFund = data.accounts.fund
            }
            if (data.accounts.accountNoSuffix === null) {
              this.paymentForm.oldAccountNoSuffix = ''
            } else {
              this.paymentForm.oldAccountNoSuffix = data.accounts.accountNoSuffix
            }
            this.paymentForm.policyNo = ''
            if (data.accounts.polNo !== null && data.accounts.polNo !== '') {
              this.paymentForm.policyNo = data.accounts.polNo + '-' + data.accounts.renCnt + '-' + data.accounts.endtCnt
            }
            this.fetchUserDetails(data.accounts.createdBy).then(result => {
              this.paymentForm.createdBy = result
            })
            this.paymentForm.createdDate = formatDisplayDateTime4(data.accounts.createdDate)
            this.fetchUserDetails(data.accounts.modifiedBy).then(result => {
              this.paymentForm.modifiedBy = result
            })
            this.paymentForm.modifiedDate = formatDisplayDateTime4(data.accounts.modifiedDate)
          }
        } catch (err) {
          const { data } = err.response
          this.showPromptLeave = true
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      } else {
        this.showPromptLeave = true
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getChequeNoForm() {
      const apiUrl = this.$store.state.payment.menu.chequeNoForm
      if (apiUrl) {
        try {
          const response = await this.$http.get(expandURI(apiUrl))
          const { data } = response.data
          if (data) {
            this.chequeNoForm = data
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async getAcctMasterPaytChequeNo(form) {
      const apiUrl = this.$store.state.payment.menu.checkAcctMasterCheqNo
      if (apiUrl) {
        try {
          const requestParams = {
            chequeNo: form.chequeNo,
            bankNo: form.fromBank,
            branch: form.branch,
            tranType: form.transactionType,
            docNo: form.documentNo,
            acctNo: form.accountNo,
            fund: form.fund,
            suffix: form.accountNoSuffix
          }
          const response = await this.$http.get(expandURI(apiUrl, requestParams))
          const { data } = response.data
          if (data) {
            this.isExistChequeNo = data
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async doMatchNow(data) {
      await Promise.all([this.$store.dispatch('matchingInit')])
      const commit = {
        action: 'M',
        branch: data.branch,
        acctNo: data.accountNo,
        type: data.transactionType,
        docNo: data.documentNo,
        fund: data.fund,
        suffix: data.accountNoSuffix,
        frmModule: 'AcctPayt',
        printDoc: data.printDocument
      }
      this.$store.commit(MATCH_LOAD_DATA, commit)
      this.$router.push({ name: 'reviewMatchTransaction' })
    },
    async fetchBranch() {
      this.branches = []
      let apiUrl = this.$store.state.payment.menu.fetchBranch
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.branches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchFund() {
      this.funds = []
      let apiUrl = this.$store.state.payment.menu.fetchFund
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.funds.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAccount() {
      this.accounts = []
      let apiUrl = this.$store.state.payment.menu.fetchAccount
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.accounts.push(data)
              })
            }
          }
        })
      }
    },
    async fetchBank() {
      this.banks = []
      let apiUrl = this.$store.state.payment.menu.fetchBank
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.banks.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAcctNoSfx() {
      this.acctnosuffixes = []
      let apiUrl = this.$store.state.payment.menu.fetchAcctNoSfx
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.acctnosuffixes.push(data)
              })
            }
          }
        })
      }
    },
    async fetchGstStatus() {
      this.gstStatuses = []
      let apiUrl = this.$store.state.payment.menu.fetchGstStatus
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.gstStatuses.push(data)
              })
            }
          }
        })
      }
    },
    async fetchCurrency() {
      this.currencies = []
      let apiUrl = this.$store.state.payment.menu.fetchCurrency
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.currencies.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAmountCurrency() {
      this.amountcurrencies = []
      let apiUrl = this.$store.state.payment.menu.fetchAmountCurrency
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.amountcurrencies.push(data)
              })
            }
          }
        })
      }
    },
    async fetchDepartment() {
      this.departments = []
      let apiUrl = this.$store.state.payment.menu.fetchDepartment
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.departments.push(data)
              })
            }
          }
        })
      }
    },
    async fetchCashBook() {
      this.cashbooks = []
      let apiUrl = this.$store.state.payment.menu.fetchCashBook
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.cashbooks.push(data)
              })
            }
          }
        })
      }
    },
    async fetchBranchWBranchUserId() {
      this.branches = []
      let apiUrl = this.$store.state.payment.menu.fetchBranchWBranchUserId
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.branches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchUserDetails(userid) {
      let username = '-'
      let apiUrl = this.$store.state.payment.menu.fetchUserDetails
      if (apiUrl) {
        const expand = {
          userId: userid
        }
        await this.$http.get(expandURI(apiUrl, expand)).then(result => {
          if (result.data) {
            const data = result.data.dataMap
            const { userDetails } = data
            username = userDetails.firstName
            if (userDetails.lastName !== null || userDetails.lastName !== '') {
              username = username + ' ' + userDetails.lastName
            }
          }
        })
      }
      return username
    },
    checkFiltersNullValue() {
      const ignore = ['transactionType', 'fund', 'transactionDate', 'actionName']
      return Object.keys(this.paymentForm)
        .filter(key => !ignore.includes(key))
        .every(key => this.paymentForm[key] === null ||
          this.paymentForm[key] === '' ||
          this.paymentForm[key] === undefined
        )
    },
    doExportFilters() {
      const checknull = this.checkFiltersNullValue()
      if (checknull) {
        this.exportList.promptFilterWarning = true
      } else {
        this.exportList.promptFilterWarning = false
        this.doExportList()
      }
    },
    async doExportList() {
      let columns = []
      let columnsTitle = []
      this.paymentColumns.map(t => {
        columnsTitle.push(t.text)
        columns.push(t.value)
      })
      const apiUrl = this.$store.state.payment.menu.exportAcctTypeFilteredList
      if (apiUrl) {
        try {
          let massageForm = clone(this.paymentForm)
          massageForm.acctType = 'Payment'
          massageForm.columns = columns.join(',')
          massageForm.columnsTitle = columnsTitle.join(',')
          await this.$http.post(apiUrl, massageForm)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    }
  },
  mixins: [
    MessageMixin,
    recordLockMixin,
    accountsAjaxCalls
  ]
}
